// @ts-check
import { playerBody, damagePlayer } from './power.js';
/** @typedef {import('./types.js').Level} Level */
/** @typedef {import('./player.js').Position} Player */
/** @param {import('./types.js').Rect} a @param {import('./types.js').Rect} b */
export function overlaps(a, b) { return a.x + a.width > b.x + 0.01 && a.x < b.x + b.width - 0.01 && a.y + a.height > b.y + 0.01 && a.y < b.y + b.height - 0.01; }
/** @param {import('./types.js').MechanismDefinition} definition @returns {import('./types.js').Mechanism} */
export function createMechanism(definition) {
  return { definition, age: 0, warning: 0, engaged: false, enabled: !definition.alternate, dx: 0, dy: 0,
    solid: { ...definition, kind: 'compute', mechanism: definition.id, active: !['vent', 'scanner', 'belt'].includes(definition.kind) && !definition.alternate, used: false, bump: 0 } };
}
/** Signals can be struck again after a prize is spent. @param {Level} level @param {string} group @param {import('./types.js').EventSink} [emit] */
export function signal(level, group, emit) {
  level.signals[group] = !level.signals[group];
  for (const mechanism of level.mechanisms) if (mechanism.definition.group === group) {
    mechanism.enabled = !mechanism.enabled;
    mechanism.engaged = false; mechanism.age = 0;
  }
  emit?.({ type: 'signal', x: 0, y: 0, entityId: group });
}
/** Shared hazard damage keeps immunity and the power ladder consistent.
 * @param {Level} level @param {Player} player @param {import('./types.js').Rect} source @param {string} reason @param {import('./types.js').EventSink} [emit]
 */
export function hurt(level, player, source, reason, emit) {
  damagePlayer(level, player, { ...source, id: 'hazard', vx: 0, vy: 0, kind: 'sentinel', mode: 'walk', dead: 0, awake: true, grace: 0 }, emit);
  if (level.status === 'dead') level.reason = reason;
}
/** Move kinematic geometry before player integration. A blocked push yields the
 * platform for this step, keeping QP out of both the platform and the wall.
 * @param {Level} level @param {Player} player @param {number} dt @param {import('./types.js').EventSink} [emit]
 */
export function updateMechanisms(level, player, dt, emit) {
  for (const m of level.mechanisms) {
    const d = m.definition, s = m.solid, body = { ...playerBody(player), x: player.x, y: player.y };
    const standing = player.grounded && body.x + body.width > s.x && body.x < s.x + s.width && Math.abs(body.y - s.y - s.height) < 0.1;
    const clock = ((level.time + (d.offset ?? 0)) % d.period) / d.period;
    m.warning = 0; m.dx = m.dy = 0;
    if (d.kind === 'spring') {
      m.age = Math.max(0, m.age - dt);
      m.warning = m.age / .35;
      if (standing && m.age === 0) {
        Object.assign(player, { vy: level.definition.quantum ? 680 : 850, jumps: 1, grounded: false, crouching: false, sliding: false, pound: 'none' });
        m.age = .35;
        emit?.({ type: 'jump', x: player.x, y: player.y });
      }
    } else if (d.kind === 'carrier') {
      const phase = (1 - Math.cos(clock * Math.PI * 2)) / 2;
      const x = d.x + (d.travelX ?? 0) * phase, y = d.y + (d.travelY ?? 0) * phase;
      const dx = x - s.x, dy = y - s.y;
      const moved = { ...s, x, y };
      const next = { ...body };
      if (standing) { next.x += dx; next.y += dy; }
      else if (overlaps(body, moved)) {
        if (Math.abs(dx) > Math.abs(dy)) next.x = dx > 0 ? x + s.width : x - body.width;
        else next.y = dy > 0 ? y + s.height : y - body.height;
      }
      const blocked = (next.x !== body.x || next.y !== body.y) && (next.x < 14 || next.x + body.width > level.width - 14 || next.y < 0 || [...level.solids, ...level.dynamic].some(other => other !== s && other.active && overlaps(next, other)));
      if (!blocked) { m.dx = dx; m.dy = dy; s.x = x; s.y = y; player.x = next.x; player.y = next.y; }
    } else if (d.kind === 'memory') {
      if (standing) m.engaged = true;
      if (m.engaged) m.age += dt;
      s.active = m.age < d.period * 0.6;
      m.warning = m.engaged ? Math.min(1, m.age / (d.period * 0.6)) : 0;
      if (m.age >= d.period) { m.engaged = false; m.age = 0; }
    } else if (d.kind === 'gate' || d.kind === 'phase') {
      const wantsSolid = d.kind === 'phase' ? m.enabled : clock < 0.48;
      // No instant materialization through a player who is crossing the plane.
      s.active = wantsSolid && (s.active || !overlaps(body, s));
      m.warning = d.kind === 'gate' ? Math.max(0, (clock - 0.78) / 0.22) : Number(m.enabled);
    } else if (d.kind === 'vent') {
      m.enabled = clock < 0.72;
      m.warning = m.enabled ? 1 : Math.max(0, (clock - 0.8) * 5);
      if (m.enabled && overlaps(body, d) && player.pound === 'none') {
        player.vy = Math.min(320, Math.max(player.vy, 70) + 2400 * dt); player.grounded = false;
      }
    } else if (d.kind === 'scanner') {
      m.enabled = clock >= 0.62 && clock < 0.84;
      m.warning = clock >= 0.32 && clock < 0.62 ? (clock - 0.32) / 0.3 : 0;
      const cover = level.solids.some(solid => solid.active && solid.x > d.x && solid.x + solid.width < body.x + 1 && solid.y <= d.y && solid.y + solid.height >= d.y + d.height);
      if (m.enabled && !cover && overlaps(body, d)) hurt(level, player, d, 'Caught in a security scan', emit);
    } else if (d.kind === 'belt' && standingOnFloor(body, d)) {
      const next = { ...body, x: body.x + (d.travelX ?? 55) * dt };
      if (!level.solids.some(solid => solid.active && overlaps(next, solid))) player.x = Math.max(14, Math.min(level.width - body.width - 14, next.x));
    }
  }
}
/** @param {import('./types.js').Rect} body @param {import('./types.js').Rect} region */
function standingOnFloor(body, region) { return Math.abs(body.y - region.y) < 0.1 && body.x + body.width > region.x && body.x < region.x + region.width; }
